import type { CopilotLiveDataStatus } from './copilot.types';

/** GrowthBook boolean flag gating the whole feature. */
export const COPILOT_FEATURE_FLAG = 'app-web-copilot';

/** Boolean GrowthBook flags forwarded to the Agent Gateway. Explicit — do not dump all flags. */
export const COPILOT_GATEWAY_BOOLEAN_FLAGS = [COPILOT_FEATURE_FLAG] as const;

/**
 * GrowthBook boolean — show top token consumers on Copilot Usage.
 * Default off until the flag is created / targeted in GrowthBook.
 */
export const COPILOT_USAGE_TOP_USERS_FLAG = 'copilotTokenUsageTopUsers';

/**
 * GrowthBook / featureFlags key for entitled packaged skills (string[]).
 * Must match agent-gateway `AGENT_SKILLS_GB_FLAG` default (`agentSkills`).
 */
export const COPILOT_AGENT_SKILLS_FLAG = 'agentSkills';

/**
 * Phase 1 pilot skills sent on every agent query until GrowthBook returns a list.
 * Keep in sync with td-agent-gateway `src/skills/skills-catalog.json` ids.
 */
export const COPILOT_PHASE1_AGENT_SKILLS: readonly string[] = [
  'call_center_adherence',
  'manager_coaching',
];

/**
 * Resolve entitled skill ids for the Agent Gateway context.
 * Prefers GrowthBook `agentSkills` (array or JSON/CSV string); falls back to Phase 1 pilot list.
 */
export function resolveCopilotAgentSkills(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    const ids = raw.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
    if (ids.length) return ids;
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        const ids = parsed.filter(
          (v): v is string => typeof v === 'string' && v.trim().length > 0,
        );
        if (ids.length) return ids;
      }
    } catch {
      const ids = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (ids.length) return ids;
    }
  }
  return [...COPILOT_PHASE1_AGENT_SKILLS];
}

/** localStorage key for the persisted agent thread id. */
export const COPILOT_THREAD_STORAGE_KEY = 'copilotThreadId';

/** localStorage key — multi-conversation tabs (V1). */
export const COPILOT_CONVERSATIONS_STORAGE_KEY = 'tempoConversationsV1';

/** Max conversation tabs kept in localStorage. */
export const COPILOT_MAX_CONVERSATIONS = 8;

/** Max messages persisted per conversation tab. */
export const COPILOT_MAX_MESSAGES_PER_CONVERSATION = 40;

/** localStorage key — user acknowledged live-data MCP key disclosure. */
export const COPILOT_MCP_DISCLOSURE_KEY = 'tempoMcpDisclosureAck';

/** localStorage key — user dismissed first-run composer tips. */
export const COPILOT_FIRST_RUN_TIPS_KEY = 'tempoFirstRunTipsDismissed';

/** localStorage key — user has seen the large empty-state badge. */
export const COPILOT_EMPTY_STATE_SEEN_KEY = 'tempoEmptyStateSeen';

/** localStorage key — persisted Tempo panel width in pixels. */
export const COPILOT_PANEL_WIDTH_STORAGE_KEY = 'tempoPanelWidth';

/** localStorage key — Tempo personality + decision engine client preferences. */
export const COPILOT_AGENT_SETTINGS_KEY = 'tempoAgentSettings';

/**
 * When false (default), hide the advisor picker and do not send
 * `advisorPersona` / `tempoPersonality` feature flags — the gateway uses its
 * blended Tempo default (`AGENT_MULTI_PERSONA_ENABLED=false`).
 * Set true to restore Kevin/Maya/Alex/Sam selection in the UI.
 */
export const COPILOT_MULTI_PERSONA_ENABLED = false;

export const COPILOT_PANEL_DEFAULT_WIDTH = 420;
export const COPILOT_PANEL_MIN_WIDTH = 320;
export const COPILOT_PANEL_MAX_WIDTH = 720;

/** Default empty-state starter prompts when no route-specific set matches. */
export const COPILOT_DEFAULT_SUGGESTION_KEYS = [
  'copilot.suggestions.reports',
  'copilot.suggestions.team',
  'copilot.suggestions.getStarted',
] as const;

export interface CopilotSuggestionMeta {
  key: string;
  icon: string;
}

/** Visual metadata for empty-state suggestion chips. */
export const COPILOT_SUGGESTION_META: Record<string, { icon: string }> = {
  'copilot.suggestions.reports': { icon: 'bar_chart' },
  'copilot.suggestions.team': { icon: 'group_add' },
  'copilot.suggestions.getStarted': { icon: 'help_outline' },
  'copilot.suggestions.liveData.hours': { icon: 'schedule' },
  'copilot.suggestions.liveData.compare': { icon: 'trending_up' },
  'copilot.suggestions.liveData.idle': { icon: 'timer_off' },
  'copilot.suggestions.manager.coaching': { icon: 'psychology' },
  'copilot.suggestions.user.myHours': { icon: 'person' },
  'copilot.suggestions.route.timelineIdle': { icon: 'timer_off' },
  'copilot.suggestions.route.timelineApps': { icon: 'apps' },
  'copilot.suggestions.route.timelineSessions': { icon: 'timeline' },
  'copilot.suggestions.route.activityLevel': { icon: 'speed' },
  'copilot.suggestions.route.activityCompare': { icon: 'compare_arrows' },
  'copilot.suggestions.route.manageUsersRoles': { icon: 'admin_panel_settings' },
  'copilot.suggestions.route.dashboardOverview': { icon: 'dashboard' },
  'copilot.suggestions.route.dashboardCheckIn': { icon: 'person_search' },
  'copilot.suggestions.route.reportsExplain': { icon: 'analytics' },
  'copilot.suggestions.route.hoursTrackedTotal': { icon: 'schedule' },
  'copilot.suggestions.route.hoursTrackedCompare': { icon: 'trending_up' },
  'copilot.suggestions.route.projectsBreakdown': { icon: 'donut_small' },
  'copilot.suggestions.route.projectsLeaderboard': { icon: 'emoji_events' },
  'copilot.suggestions.route.webAppsUsed': { icon: 'language' },
  'copilot.suggestions.route.webTopSites': { icon: 'public' },
  'copilot.suggestions.route.attendanceLate': { icon: 'event_available' },
  'copilot.suggestions.route.schedulesLeave': { icon: 'calendar_month' },
  'copilot.suggestions.route.meetingsTime': { icon: 'groups' },
  'copilot.suggestions.route.officeRemoteShare': { icon: 'home_work' },
  'copilot.suggestions.route.officeRemoteHybrid': { icon: 'swap_horiz' },
  'copilot.suggestions.route.invitePeople': { icon: 'person_add' },
  'copilot.suggestions.route.inviteSteps': { icon: 'mail' },
  'copilot.suggestions.route.userGroups': { icon: 'groups' },
  'copilot.suggestions.route.addManagers': { icon: 'supervisor_account' },
  'copilot.suggestions.route.projectsTasks': { icon: 'assignment' },
  'copilot.suggestions.route.createTask': { icon: 'add_task' },
  'copilot.suggestions.route.screencasts': { icon: 'screenshot_monitor' },
  'copilot.suggestions.route.screencastPrivacy': { icon: 'visibility' },
  'copilot.suggestions.route.notifications': { icon: 'notifications' },
  'copilot.suggestions.route.insights': { icon: 'insights' },
  'copilot.suggestions.route.companySettings': { icon: 'settings' },
  'copilot.suggestions.route.trackingSettings': { icon: 'tune' },
  'copilot.suggestions.route.billingPlan': { icon: 'credit_card' },
  'copilot.suggestions.route.billingSeats': { icon: 'airline_seat_recline_extra' },
  'copilot.suggestions.route.productivityRatings': { icon: 'category' },
  'copilot.suggestions.route.breaks': { icon: 'free_breakfast' },
  'copilot.suggestions.route.approvals': { icon: 'fact_check' },
  'copilot.suggestions.route.aiImpact': { icon: 'auto_awesome' },
  'copilot.suggestions.route.unusualActivity': { icon: 'warning' },
  'copilot.suggestions.route.webAppUsage': { icon: 'language' },
  'copilot.suggestions.route.softwareCost': { icon: 'payments' },
  'copilot.suggestions.route.payroll': { icon: 'account_balance' },
  'copilot.suggestions.route.integrations': { icon: 'extension' },
  'copilot.suggestions.route.editTime': { icon: 'edit_calendar' },
  'copilot.suggestions.route.downloads': { icon: 'download' },
  'copilot.suggestions.route.benchmarks': { icon: 'leaderboard' },
  'copilot.suggestions.route.profile': { icon: 'manage_accounts' },
  'copilot.suggestions.route.csvExport': { icon: 'file_download' },
  'copilot.suggestions.route.individualDashboard': { icon: 'person' },
  'copilot.suggestions.route.executiveDashboard': { icon: 'insights' },
};

/**
 * Route-aware empty-state prompts. More specific prefixes must come first;
 * first matching prefix wins. Unknown routes use {@link COPILOT_DEFAULT_SUGGESTION_KEYS}
 * (or live-data / role defaults when connected).
 */
export const COPILOT_ROUTE_SUGGESTIONS: ReadonlyArray<{
  prefix: string;
  keys: readonly string[];
}> = [
  // --- Dashboards (specific before /dashboard) --------------------------------
  {
    prefix: '/dashboard-individual',
    keys: [
      'copilot.suggestions.route.individualDashboard',
      'copilot.suggestions.user.myHours',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/dashboard-executive',
    keys: [
      'copilot.suggestions.route.executiveDashboard',
      'copilot.suggestions.route.dashboardOverview',
      'copilot.suggestions.liveData.compare',
    ],
  },
  {
    prefix: '/dashboard',
    keys: [
      'copilot.suggestions.route.dashboardOverview',
      'copilot.suggestions.route.dashboardCheckIn',
      'copilot.suggestions.liveData.compare',
    ],
  },

  // --- Core reports -----------------------------------------------------------
  {
    prefix: '/timeline',
    keys: [
      'copilot.suggestions.route.timelineIdle',
      'copilot.suggestions.route.timelineApps',
      'copilot.suggestions.route.timelineSessions',
    ],
  },
  {
    prefix: '/activity-summary',
    keys: [
      'copilot.suggestions.route.activityLevel',
      'copilot.suggestions.route.activityCompare',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/hours-tracked',
    keys: [
      'copilot.suggestions.route.hoursTrackedTotal',
      'copilot.suggestions.route.hoursTrackedCompare',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/projects-report',
    keys: [
      'copilot.suggestions.route.projectsBreakdown',
      'copilot.suggestions.route.projectsLeaderboard',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/internet-report',
    keys: [
      'copilot.suggestions.route.webAppsUsed',
      'copilot.suggestions.route.webTopSites',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/web-app-usage',
    keys: [
      'copilot.suggestions.route.webAppUsage',
      'copilot.suggestions.route.webTopSites',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/attendance',
    keys: [
      'copilot.suggestions.route.attendanceLate',
      'copilot.suggestions.route.schedulesLeave',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/schedules',
    keys: [
      'copilot.suggestions.route.schedulesLeave',
      'copilot.suggestions.route.attendanceLate',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/meeting-insights',
    keys: [
      'copilot.suggestions.route.meetingsTime',
      'copilot.suggestions.liveData.compare',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/office-vs-remote',
    keys: [
      'copilot.suggestions.route.officeRemoteShare',
      'copilot.suggestions.route.officeRemoteHybrid',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/unusual-activity',
    keys: [
      'copilot.suggestions.route.unusualActivity',
      'copilot.suggestions.manager.coaching',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/software-cost-insights',
    keys: [
      'copilot.suggestions.route.softwareCost',
      'copilot.suggestions.route.webAppsUsed',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/benchmarks',
    keys: [
      'copilot.suggestions.route.benchmarks',
      'copilot.suggestions.liveData.compare',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/workforce-intelligence',
    keys: [
      'copilot.suggestions.route.benchmarks',
      'copilot.suggestions.route.dashboardOverview',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/payroll',
    keys: [
      'copilot.suggestions.route.payroll',
      'copilot.suggestions.route.hoursTrackedTotal',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/csv',
    keys: [
      'copilot.suggestions.route.csvExport',
      'copilot.suggestions.reports',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/reports',
    keys: [
      'copilot.suggestions.reports',
      'copilot.suggestions.route.reportsExplain',
      'copilot.suggestions.getStarted',
    ],
  },

  // --- People & setup ---------------------------------------------------------
  {
    prefix: '/invite',
    keys: [
      'copilot.suggestions.route.invitePeople',
      'copilot.suggestions.route.inviteSteps',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/manage-users',
    keys: [
      'copilot.suggestions.team',
      'copilot.suggestions.route.manageUsersRoles',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/manage-user-groups',
    keys: [
      'copilot.suggestions.route.userGroups',
      'copilot.suggestions.team',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/add-managers',
    keys: [
      'copilot.suggestions.route.addManagers',
      'copilot.suggestions.route.manageUsersRoles',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/projects-and-tasks',
    keys: [
      'copilot.suggestions.route.projectsTasks',
      'copilot.suggestions.route.createTask',
      'copilot.suggestions.getStarted',
    ],
  },

  // --- Settings & product surfaces --------------------------------------------
  {
    prefix: '/screencasts',
    keys: [
      'copilot.suggestions.route.screencasts',
      'copilot.suggestions.route.screencastPrivacy',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/notifications',
    keys: [
      'copilot.suggestions.route.notifications',
      'copilot.suggestions.route.insights',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/signals',
    keys: [
      'copilot.suggestions.route.insights',
      'copilot.suggestions.liveData.idle',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/company',
    keys: [
      'copilot.suggestions.route.companySettings',
      'copilot.suggestions.route.trackingSettings',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/billing',
    keys: [
      'copilot.suggestions.route.billingPlan',
      'copilot.suggestions.route.billingSeats',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/new-billing',
    keys: [
      'copilot.suggestions.route.billingPlan',
      'copilot.suggestions.route.billingSeats',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/productivity-ratings',
    keys: [
      'copilot.suggestions.route.productivityRatings',
      'copilot.suggestions.route.webAppsUsed',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/productivity',
    keys: [
      'copilot.suggestions.route.productivityRatings',
      'copilot.suggestions.route.activityLevel',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/breaks',
    keys: [
      'copilot.suggestions.route.breaks',
      'copilot.suggestions.route.schedulesLeave',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/approvals',
    keys: [
      'copilot.suggestions.route.approvals',
      'copilot.suggestions.route.editTime',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/ai-impact',
    keys: [
      'copilot.suggestions.route.aiImpact',
      'copilot.suggestions.liveData.compare',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/integrations',
    keys: [
      'copilot.suggestions.route.integrations',
      'copilot.suggestions.getStarted',
      'copilot.suggestions.reports',
    ],
  },
  {
    prefix: '/edit-time',
    keys: [
      'copilot.suggestions.route.editTime',
      'copilot.suggestions.route.approvals',
      'copilot.suggestions.getStarted',
    ],
  },
  {
    prefix: '/downloads',
    keys: [
      'copilot.suggestions.route.downloads',
      'copilot.suggestions.getStarted',
      'copilot.suggestions.team',
    ],
  },
  {
    prefix: '/profile-edit',
    keys: [
      'copilot.suggestions.route.profile',
      'copilot.suggestions.getStarted',
      'copilot.suggestions.user.myHours',
    ],
  },
];

const COPILOT_MANAGER_SUGGESTION_KEYS = [
  'copilot.suggestions.liveData.hours',
  'copilot.suggestions.manager.coaching',
  'copilot.suggestions.reports',
] as const;

const COPILOT_USER_SUGGESTION_KEYS = [
  'copilot.suggestions.user.myHours',
  'copilot.suggestions.reports',
  'copilot.suggestions.getStarted',
] as const;

export interface CopilotSuggestionContext {
  route: string;
  liveDataStatus: CopilotLiveDataStatus;
  isManager: boolean;
}

function routeSuggestionKeys(route: string): readonly string[] {
  const normalized = (route ?? '').split('?')[0];
  const match = COPILOT_ROUTE_SUGGESTIONS.find((entry) =>
    normalized.startsWith(entry.prefix),
  );
  return match?.keys ?? COPILOT_DEFAULT_SUGGESTION_KEYS;
}

/** True when the current route has a dedicated starter set (not the global default). */
export function copilotHasRouteSpecificSuggestions(route: string): boolean {
  const normalized = (route ?? '').split('?')[0];
  return COPILOT_ROUTE_SUGGESTIONS.some((entry) => normalized.startsWith(entry.prefix));
}

/** Resolve starter-prompt i18n keys for the current app route. */
export function copilotSuggestionsForRoute(route: string): readonly string[] {
  return routeSuggestionKeys(route);
}

/**
 * Resolve starter prompts using route, live-data state, and user role.
 * Route-specific sets win; otherwise live-data and role tailor the default list.
 */
export function copilotSuggestionsForContext(ctx: CopilotSuggestionContext): readonly string[] {
  if (copilotHasRouteSpecificSuggestions(ctx.route)) {
    return routeSuggestionKeys(ctx.route);
  }
  if (ctx.liveDataStatus === 'connected') {
    return ctx.isManager ? COPILOT_MANAGER_SUGGESTION_KEYS : COPILOT_USER_SUGGESTION_KEYS;
  }
  return COPILOT_DEFAULT_SUGGESTION_KEYS;
}

export function copilotSuggestionMeta(key: string): CopilotSuggestionMeta {
  const meta = COPILOT_SUGGESTION_META[key];
  return { key, icon: meta?.icon ?? 'chat_bubble_outline' };
}

/**
 * Push layout: minimum width the report content keeps when the classic panel
 * reserves space. Below this the panel shrinks instead. Content squeezed past
 * its natural layout width falls back to horizontal scrolling (styles.scss).
 */
export const COPILOT_PUSH_MIN_CONTENT_WIDTH = 1024;
